<template>
  <div v-if="!data.assignedCoachLoaded">
    <iLoaderSimple :loading-animation-duration-ms="3000">
      <template #title>Just a moment!</template>
      <template #subtitle>We are preparing your plan...</template>
    </iLoaderSimple>
  </div>
  <div
    v-else
    class="i-select-plan"
  >
    <iBackground
      :background-image="bgGradient"
      image-position="center center"
    />
    <iSelectPlanCountdownNav
      v-if="assignedCoach?.fullName && !userStore.isCoachingHasEngaged && data.countdownTimer > 0"
      @update-countdown="data.countdownTimer = $event"
    />
    <iReportNavbar
      :core-items="navigationItemsData.coreItems"
      :profile-items="navigationItemsData.profileItems"
      :support-items="navigationItemsData.supportItems"
    />

    <div>
      <iReserveBooking
        v-if="!userStore.isCoachingHasEngaged"
        :booking="data.upcomingUnpaidCoaching || undefined"
        :checkout-trigerred="data.bookingTriggered"
        :show-processing="data.showProcessing"
        @fetch-booking="fetchUpcomingUnpaidCoachingBooking($event)"
        @trigger-checkout="data.checkoutTriggered = true"
        @revert-triggers="revertTriggers"
      />
      <iPlanOptions
        :coaching-plans="coachingPlans"
        :chosen-plan="data.chosenPlan"
        :addon-selected="data.coachingAddonSelected"
        :is-unpaid-executive-coaching="isUnpaidExecutivePotential"
        :discount-identifier="dicountIdentifier"
        @addon-toggled="data.coachingAddonSelected = $event"
        @plan-selected="setChosenPlan($event)"
      />
      <iCoachingBenefits />
      <iCoachSummary v-if="!isUnpaidExecutivePotential" />
    </div>

    <iSelectPlanFooter
      :coaching-plans="coachingPlans"
      :chosen-plan="data.chosenPlan"
      :disable-payment-until-booking="!userStore.isCoachingHasEngaged && !data.upcomingUnpaidCoaching"
      :checkout-trigerred="data.checkoutTriggered"
      :addon-selected="data.coachingAddonSelected"
      :discount-identifier="dicountIdentifier"
      @trigger-booking="data.bookingTriggered = true"
    />

    <iBaseModal
      visible
      class="zigpoll-modal"
      :class="{ visible: data.zigpollModalVisible }"
      @click-on-close="data.zigpollModalVisible = false"
    >
      <div class="zigpoll-modal-wrap">
        <div class="zigpoll-modal-text">You can close this modal now!</div>
        <div id="zigpoll-embed-target"></div>
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, onMounted, reactive } from "vue"
import { useRoute } from "vue-router"
import { useCoachingStore } from "@/stores/coaching"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { breakpointLg } from "@/styles/variables"

import onAwayChangeDocumentTitle from "@/composables/onAwayChangeDocumentTitle"
import navigationItems from "@/composables/navigation"
import { GlobalVariables } from "@/helpers/globalVariables"
import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"

// COMPONENTS
import iBaseModal from "@/components/base/BaseModal.vue"
import iLoaderSimple from "@/components/global/LoaderSimple.vue"
import iCoachSummary from "@/components/coaching/selectPlan/CoachSummary.vue"
import iCoachingBenefits from "@/components/coaching/selectPlan/CoachingBenefits.vue"
import iPlanOptions from "@/components/coaching/selectPlan/PlanOptions.vue"
import iReserveBooking from "@/components/coaching/selectPlan/ReserveBooking.vue"
import iSelectPlanCountdownNav from "@/components/coaching/selectPlan/SelectPlanCountdownNav.vue"
import iSelectPlanFooter from "@/components/coaching/selectPlan/SelectPlanFooter.vue"
import iReportNavbar from "@/components/report/navbar/ReportNavbar.vue"
import iBackground from "@/components/global/Background.vue"

// ASSETS
import bgGradient from "@/assets/confirm/bg-gradient-multi.png"

// TYPES
import ReportStatus from "@/shared/enums/ReportStatus"
import StripePrice from "@/shared/enums/StripePrice"
import CalcomBooking from "@/shared/types/CalcomBooking"
import { CheckoutSessionItem } from "@/shared/types/CheckoutSession"
import type { CoachingPlans } from "@/types"

// STORES
const coachingStore = useCoachingStore()
const userStore = useUserStore()
const reportStore = useReportStore()
const activeRoute = useRoute()
const dicountIdentifier =
  activeRoute.query["promo_code"]?.toString() || sessionStorage.getItem("promo_code") || undefined
const datefromSelectCoach = activeRoute.query["date"]?.toString() || undefined

// DATA
const data = reactive({
  showProcessing: false,
  chosenPlan: StripePrice.COACHING_CORE,
  assignedCoachLoaded: false,
  zigpollModalVisible: false,
  zigpollModalDelayMs: 45000, // 45 seconds
  coachingAddonSelected: false,
  countdownTimer: parseInt(localStorage.getItem("coaching-countdown") || ""),
  upcomingUnpaidCoaching: null as CalcomBooking | null,
  checkoutTriggered: false,
  bookingTriggered: false
})

// FUNCTIONS
/**
 * Send a GTM event when the user views the coaching plans
 */
const sendGTMEvent = async () => {
  try {
    const response = await api.stripe.getPrices([
      StripePrice.COACHING_DISCOVERY,
      StripePrice.COACHING_CORE,
      StripePrice.COACHING_ACCELERATED,
      StripePrice.RESUME_COACHING_ADDON,
      StripePrice.COACHING_DISCOVERY_LOW,
      StripePrice.COACHING_DISCOVERY_ONE
    ])
    const pricesArr = response.prices
    const itemsShownToClient: CheckoutSessionItem[] = []

    for (const price of pricesArr) {
      const coachingPlan = coachingPlans.value[price.uuid]
      if (
        (coachingPlan && coachingPlan.isVisible) ||
        (price.uuid === StripePrice.RESUME_COACHING_ADDON && !userStore.userData?.hasResumePackage)
      ) {
        const isCore = coachingPlan?.id === StripePrice.COACHING_CORE
        const isAccelerated = coachingPlan?.id === StripePrice.COACHING_ACCELERATED

        itemsShownToClient.push({
          id: price.productId,
          price: (price.price - (userStore.isCoachingHasEngaged ? 0 : isAccelerated ? 7000 : isCore ? 3000 : 0)) / 100,
          quantity: 1,
          name: price.name
        })
      }
    }

    if (itemsShownToClient.length > 0) {
      await GtmService.viewItemListEvent(UserFlow.COACHING, itemsShownToClient)
    }
  } catch (error) {
    console.error("Failed to load prices", error)
  }
}

const revertTriggers = () => {
  data.bookingTriggered = false
  data.checkoutTriggered = false
}

// COMPUTED
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const navigationItemsData = computed(() => navigationItems())
const isUnpaidExecutivePotential = computed(
  () => userStore.isExecutiveCoachingPotential && !userStore.isCoachingHasEngaged
)
const showRegularSubscriptionPlans = computed(() =>
  userStore.isCoachingHasEngaged
    ? !userStore.hasPurchasedExecutiveSubscription
    : !userStore.isExecutiveCoachingPotential
)
const coachingPlans: ComputedRef<CoachingPlans> = computed(() => {
  return {
    [StripePrice.COACHING_DISCOVERY_ONE]: {
      id: StripePrice.COACHING_DISCOVERY_ONE,
      details: {
        "30 minute session of 1:1 coaching": true,
        "Career report with personalized insights": true,
        "Diagnostic and foundational strategy outline": true,
        "Weekly live group coaching": userStore.isActiveReportSubscriber ? true : false,
        "Custom-built action plan": false,
        "Mock interviews with your coach": false,
        "Resume and LinkedIn guidance": false
      },
      discounted: "",
      isSubscription: false,
      isVisible: !userStore.isCoachingHasEngaged && !userStore.isExecutiveCoachingPotential,
      stripePrice: StripePrice.COACHING_DISCOVERY_ONE,
      upgradeTier: ReportStatus.PREMIUM,
      price: "149.90",
      title: "Discovery Session",
      subtitle: "One Time Pre-plan stage"
    },
    [StripePrice.COACHING_DISCOVERY_LOW]: {
      id: StripePrice.COACHING_DISCOVERY_LOW,
      details: {
        "Up to 1 hour of 1:1 coaching": true,
        "Career report with personalized insights": true,
        "Diagnostic and foundational strategy outline": true,
        "Weekly live group coaching": true,
        "Custom-built action plan": false,
        "Mock interviews with your coach": false,
        "Resume and LinkedIn guidance": false
      },
      discounted: "",
      isSubscription: false,
      isVisible: isUnpaidExecutivePotential.value,
      stripePrice: StripePrice.COACHING_DISCOVERY_LOW,
      upgradeTier: ReportStatus.PREMIUM,
      price: "149.90",
      title: "Discovery Session",
      subtitle: "One Time Pre-plan stage"
    },
    [StripePrice.COACHING_CORE]: {
      id: StripePrice.COACHING_CORE,
      details: {
        "Up to 1 hour of 1:1 coaching each month": true,
        "Custom-built action plan": true,
        "Mock interviews with your coach": true,
        "Resume and LinkedIn guidance": true,
        "Weekly live group coaching": true,
        "Premium Career report with personalized insights": true,
        "Twice the coaching for faster results": false
      },
      discounted: !userStore.isCoachingHasEngaged ? "199.87" : "",
      isSubscription: true,
      isVisible: showRegularSubscriptionPlans.value,
      stripePrice: StripePrice.COACHING_CORE,
      upgradeTier: ReportStatus.PREMIUM,
      price: !userStore.isCoachingHasEngaged ? "149.90" : "179.90",
      title: "Core Plan",
      subtitle: "Career Essentials",
      off: !userStore.isCoachingHasEngaged ? "25%" : null,
      renews: !userStore.isCoachingHasEngaged ? "179.90" : null
    },
    [StripePrice.COACHING_ACCELERATED]: {
      id: StripePrice.COACHING_ACCELERATED,
      details: {
        "<strong>Up to 2 hours of 1:1 coaching each month</strong>": true,
        "Custom-built action plan": true,
        "Mock interviews with your coach": true,
        "Resume and LinkedIn guidance": true,
        "Weekly live group coaching": true,
        "Premium Career report with personalized insights": true,
        "<strong>Twice the coaching for faster results</strong>": true
      },
      discounted: !userStore.isCoachingHasEngaged ? "399.86" : "",
      isSubscription: true,
      isVisible: showRegularSubscriptionPlans.value,
      stripePrice: StripePrice.COACHING_ACCELERATED,
      upgradeTier: ReportStatus.PREMIUM,
      price: !userStore.isCoachingHasEngaged ? "279.90" : "349.90",
      title: "Accelerated Plan",
      subtitle: "Career Transformation",
      off: !userStore.isCoachingHasEngaged ? "30%" : null,
      renews: !userStore.isCoachingHasEngaged ? "349.90" : null
    },
    [StripePrice.COACHING_PROFESSIONAL]: {
      id: StripePrice.COACHING_PROFESSIONAL,
      details: {
        "Up to 2 hours of 1:1 coaching each month": true,
        "Custom-built action plan": true,
        "Mock interviews with your coach": true,
        "Resume and LinkedIn guidance": true,
        "Weekly live group coaching": true,
        "Premium Career report with personalized insights": true,
        "Twice the coaching for faster results": false
      },
      discounted: "",
      isSubscription: true,
      isVisible: userStore.isCoachingHasEngaged && userStore.hasPurchasedExecutiveSubscription,
      stripePrice: StripePrice.COACHING_PROFESSIONAL,
      upgradeTier: ReportStatus.PREMIUM,
      price: "549.00",
      title: "Career & Life Mastery",
      subtitle: "Bi-weekly Sessions"
    },
    [StripePrice.COACHING_EXECUTIVE_997]: {
      id: StripePrice.COACHING_EXECUTIVE_997,
      details: {
        "<strong>Up to 4 hours of 1:1 coaching each month</strong>": true,
        "Custom-built action plan": true,
        "Mock interviews with your coach": true,
        "Resume and LinkedIn guidance": true,
        "Weekly live group coaching": true,
        "Premium Career report with personalized insights": true,
        "<strong>Twice the coaching for faster results</strong>": true
      },
      discounted: "",
      isSubscription: true,
      isVisible: userStore.isCoachingHasEngaged && userStore.hasPurchasedExecutiveSubscription,
      stripePrice: StripePrice.COACHING_EXECUTIVE_997,
      upgradeTier: ReportStatus.PREMIUM,
      price: "997.00",
      title: "High-Impact Leadership & Life Alignment",
      subtitle: "Weekly Sessions"
    }
  } as CoachingPlans
})

// FUNCTIONS
const fetchUpcomingUnpaidCoachingBooking = async (delayKey = false) => {
  if (delayKey) data.showProcessing = true
  setTimeout(
    async () => {
      try {
        const upcomingBooking = await api.booking.getUpcoming({
          bookingEventTypes: ["discovery"],
          isPaid: false
        })
        data.upcomingUnpaidCoaching = upcomingBooking.booking
      } catch (error) {
        console.error("Error fetching user's upcoming unpaid coaching booking:", error)
      } finally {
        data.showProcessing = false
      }
    },
    delayKey ? 4500 : 0
  )
}

const setChosenPlan = (plan: StripePrice) => {
  data.chosenPlan = plan
}

const loadZigpoll = (delayMs: number) => {
  const isMobileView = window.innerWidth <= breakpointLg

  if (isMobileView) {
    if (window.Zigpoll) window.Zigpoll.refresh()
    reportStore.initMobileZigpollPrepayment(delayMs, GlobalVariables.zigpolls.finishedCoaching).then((modalVisible) => {
      data.zigpollModalVisible = modalVisible
    })
  } else {
    if (window.Zigpoll) reportStore.deactivateZigpollReload()
    reportStore.initDesktopZigpollPrepayment(delayMs)
  }
}

const loadCoachAndGTM = async () => {
  setTimeout(
    async () => {
      await coachingStore.loadAssignedCoach()
      data.assignedCoachLoaded = true

      // Send a GTM event when the user views the coaching plans
      sendGTMEvent()
    },
    datefromSelectCoach && assignedCoach.value.fullName ? 8000 : 500
  )
}

onAwayChangeDocumentTitle()

// LIFECYCLE HOOKS
onMounted(async () => {
  await userStore.loadUserData()
  data.chosenPlan = isUnpaidExecutivePotential.value
    ? StripePrice.COACHING_DISCOVERY_LOW
    : showRegularSubscriptionPlans.value
      ? StripePrice.COACHING_CORE
      : StripePrice.COACHING_PROFESSIONAL

  loadZigpoll(data.zigpollModalDelayMs)

  // We need to get the assigned coach again, because it will have changed after the user has booked a session
  await fetchUpcomingUnpaidCoachingBooking(!!datefromSelectCoach)
  await loadCoachAndGTM()
})
</script>

<style lang="scss" scoped>
.i-select-plan {
  .zigpoll-modal {
    visibility: hidden;

    &.visible {
      visibility: visible;
    }

    &-wrap {
      display: flex;
      flex-direction: column;
      min-height: 90px;
      max-height: 60vh;
      overflow: auto;
      position: relative;
    }

    &-text {
      font-weight: bold;
      position: absolute;
      text-align: center;
      top: 50%;
      width: 100%;
    }
  }
}
</style>
